import { useState } from "react";
import { ExternalLink, Play } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import LastReviewedBanner from "@/components/shared/LastReviewedBanner";
import { MEDIA_SERIES, MEDIA_VIDEOS, PORTFOLIO_LAST_REVIEWED } from "@/data/content";

const MEDIA = {
  player: "media-player",
  card: (id) => `media-card-${id}`,
  playlistLink: "media-playlist-link",
};

export default function Media() {
  const [active, setActive] = useState(MEDIA_VIDEOS[0]);
  const [playing, setPlaying] = useState(false);

  const select = (v) => {
    setActive(v);
    setPlaying(true);
    if (typeof window !== "undefined") {
      const el = document.getElementById("media-stage");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <PageHeader
        overline="Media"
        title={`${MEDIA_SERIES.title} — a video series`}
        lead={MEDIA_SERIES.dek}
      />

      <section id="media-stage" className="ccdr-section">
        <div className="ccdr-container">
          <LastReviewedBanner
            date={PORTFOLIO_LAST_REVIEWED}
            source={`Presented by ${MEDIA_SERIES.presenter}`}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Player */}
            <div className="lg:col-span-8">
              <div
                data-testid={MEDIA.player}
                className="aspect-video w-full bg-[#053043] border border-[#E5E7EB] relative overflow-hidden"
              >
                {playing ? (
                  <iframe
                    key={active.id}
                    src={`https://www.youtube-nocookie.com/embed/${active.id}?autoplay=1&rel=0&modestbranding=1`}
                    title={active.title}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  <button
                    onClick={() => setPlaying(true)}
                    className="absolute inset-0 w-full h-full group"
                    aria-label={`Play ${active.title}`}
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${active.id}/hqdefault.jpg`}
                      alt={active.title}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                      loading="lazy"
                    />
                    <span
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ background: "rgba(5, 48, 67, 0.35)" }}
                    >
                      <span
                        className="inline-flex items-center gap-3 px-6 py-3 bg-[#D4A373] text-[#053043]"
                        style={{ fontWeight: 500 }}
                      >
                        <Play className="w-5 h-5" fill="#053043" /> Play episode
                      </span>
                    </span>
                  </button>
                )}
              </div>

              <div className="mt-5 flex items-start justify-between gap-6 flex-wrap">
                <div>
                  <p
                    className="text-xs uppercase tracking-[0.22em] text-[#B07D4B] mb-2"
                  >
                    Now playing · {active.tag}
                  </p>
                  <h2 className="ccdr-h3">{active.title}</h2>
                  <p className="mt-3 text-[#4B5563] max-w-2xl">{active.summary}</p>
                </div>
                <a
                  href={`https://www.youtube.com/watch?v=${active.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm border-b border-[#0B4F6C] pb-0.5 text-[#0B4F6C] hover:text-[#053043]"
                >
                  Watch on YouTube <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* Episode rail */}
            <aside className="lg:col-span-4">
              <p className="ccdr-eyebrow mb-5">
                {MEDIA_VIDEOS.length} episodes · {MEDIA_SERIES.title}
              </p>

              <ul className="space-y-px bg-[#E5E7EB] border border-[#E5E7EB] max-h-[640px] overflow-y-auto">
                {MEDIA_VIDEOS.map((v, idx) => {
                  const isActive = v.id === active.id;
                  return (
                    <li key={v.id}>
                      <button
                        onClick={() => select(v)}
                        data-testid={MEDIA.card(v.id)}
                        className={`w-full text-left p-4 flex gap-4 items-start transition-colors ${
                          isActive
                            ? "bg-[#053043] text-white"
                            : "bg-white text-[#111827] hover:bg-[#FAFAFA]"
                        }`}
                      >
                        <span
                          className="text-lg tabular-nums flex-shrink-0 w-6"
                          style={{
                            color: isActive ? "var(--ccdr-accent)" : "var(--ccdr-accent-dark)",
                            fontWeight: 300,
                          }}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <div className="flex gap-3 flex-1">
                          <div className="relative w-24 flex-shrink-0">
                            <img
                              src={`https://i.ytimg.com/vi/${v.id}/mqdefault.jpg`}
                              alt=""
                              className="w-full h-14 object-cover"
                              loading="lazy"
                            />
                            <span
                              className="absolute bottom-1 right-1 text-[10px] px-1.5 py-0.5"
                              style={{
                                background: "rgba(5, 48, 67, 0.85)",
                                color: "white",
                              }}
                            >
                              {v.duration}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-sm leading-tight line-clamp-2"
                              style={{ fontWeight: 500 }}
                            >
                              {v.title}
                            </p>
                            <p
                              className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${
                                isActive ? "text-[#E9C46A]" : "text-[#9CA3AF]"
                              }`}
                            >
                              {v.tag}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <a
                href={MEDIA_SERIES.playlistUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={MEDIA.playlistLink}
                className="mt-5 ccdr-btn-outline w-full justify-center"
              >
                Open full playlist on YouTube <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </aside>
          </div>
        </div>
      </section>

      {/* Editorial context */}
      <section className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="ccdr-eyebrow mb-5">Why this series</p>
            <h2 className="ccdr-h2">Climate-smart, community-led, technology-aware.</h2>
          </div>
          <div className="lg:col-span-7 ccdr-prose">
            <p>
              The series sits squarely at the intersection the Centre tracks: climate resilience, biodiversity, and the
              responsible use of digital technology. Across seven episodes the work moves between sensor networks,
              citizen science, mobile applications, and field convening — always grounded in community.
            </p>
            <p>
              For policymakers worldwide, the relevance is direct.
              Programmes that are technically ambitious but disconnected from the people most exposed to climate risk
              tend to underperform. These films show what the alternative looks like in practice.
            </p>
          </div>
        </div>
      </section>

      <CTASection
        overline="Take this further"
        title="Read the connected briefings, or invite the Centre to convene a session."
        primary={{ to: "/insights", label: "Open Insights" }}
        secondary={{ to: "/get-involved", label: "Or talk to the Centre" }}
      />
    </>
  );
}
